import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
  DOCUMENT
} from '@angular/core';

import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TRIAL_CTA, TRIAL_LINK } from '../site.config';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { ICON_PATHS } from './icons';

interface NavItem {
  readonly label: string;
  readonly path: string;
  readonly fragment?: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Özellikler', path: '/', fragment: 'ozellikler' },
  { label: 'Fiyatlar', path: '/fiyatlar' },
  { label: 'SSS', path: '/sss' },
  { label: 'İletişim', path: '/iletisim' },
];

@Component({
    selector: 'site-header',
    // A11yModule değil yalnız CdkTrapFocus: modülün kurucusu (HighContrastModeDetector)
    // hidrasyon sırasında body'ye test öğesi ekleyip getComputedStyle ile zorunlu stil/yerleşim yapıyordu.
    imports: [RouterLink, CdkTrapFocus],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // Yapışkanlık host'ta: <header> kendi yüksekliğindeki host içinde sticky kalırsa hiç yapışmaz.
    host: { class: 'sticky top-0 z-40 block' },
    template: `
    <header class="border-0 border-b border-solid border-surface-line bg-white">
      <div class="site-container flex h-16 items-center gap-[1rem]">
        <a routerLink="/" class="flex min-h-tap items-center gap-2 font-bold text-ink no-underline">
          <img src="assets/logo.svg" alt="" width="32" height="32" class="h-8 w-8" />
          <span class="text-lg">My PT</span>
          <span class="sr-only"> — ana sayfa</span>
        </a>

        <nav aria-label="Ana menü" class="ml-auto hidden md:block">
          <ul role="list" class="flex items-center gap-1">
            @for (item of navItems; track item.label) {
              <li>
                <a
                  [routerLink]="item.path"
                  [fragment]="item.fragment"
                  class="flex min-h-tap items-center rounded-full px-[0.75rem] text-[0.95rem] font-medium text-ink no-underline hover:bg-surface-alt"
                  >{{ item.label }}</a
                >
              </li>
            }
          </ul>
        </nav>

        <!-- Sarmalayıcı şart: .mypt-site .site-btn { display } Tailwind'in "hidden"ını
             eziyordu, düğmeler telefonda da üst çubukta görünüyordu. -->
        <div class="hidden items-center gap-2 md:ml-2 md:flex">
          <a
            [routerLink]="accountPath()"
            class="site-btn"
            [class.site-btn--primary]="loggedIn()"
            [class.site-btn--secondary]="!loggedIn()"
            data-test="account-link"
            >{{ accountLabel() }}</a
          >
          @if (!loggedIn()) {
            <a
              [routerLink]="trial.path"
              [queryParams]="trial.queryParams"
              class="site-btn site-btn--primary"
              data-test="trial-link"
              >{{ trialCta }}</a
            >
          }
        </div>

        <button
          #menuButton
          type="button"
          class="ml-auto inline-flex min-h-tap min-w-tap items-center justify-center rounded-full border-[1px] border-solid border-surface-line bg-white text-ink md:hidden"
          [attr.aria-expanded]="menuOpen()"
          [attr.aria-controls]="menuOpen() ? 'site-mobile-menu' : null"
          (click)="toggleMenu()"
          data-test="menu-button"
        >
          <span class="sr-only">{{ menuOpen() ? 'Menüyü kapat' : 'Menüyü aç' }}</span>
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" class="h-6 w-6">
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="menuOpen() ? icons.close : icons.menu" />
          </svg>
        </button>
      </div>

      @if (menuOpen()) {
        <div
          id="site-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menü"
          class="fixed inset-x-0 bottom-0 top-16 z-50 overflow-y-auto overscroll-contain bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
          cdkTrapFocus
          [cdkTrapFocusAutoCapture]="true"
          data-test="mobile-menu"
        >
          <nav aria-label="Mobil menü" class="site-container py-6">
            <ul role="list" class="flex flex-col gap-1">
              @for (item of navItems; track item.label) {
                <li>
                  <a
                    [routerLink]="item.path"
                    [fragment]="item.fragment"
                    (click)="closeMenu()"
                    class="flex min-h-tap items-center rounded-xl px-[0.75rem] text-lg font-semibold text-ink no-underline hover:bg-surface-alt"
                    >{{ item.label }}</a
                  >
                </li>
              }
            </ul>
            @if (!loggedIn()) {
              <a
                [routerLink]="trial.path"
                [queryParams]="trial.queryParams"
                (click)="closeMenu()"
                class="site-btn site-btn--primary mt-6 w-full"
                >{{ trialCta }}</a
              >
            }
            <a
              [routerLink]="accountPath()"
              (click)="closeMenu()"
              class="site-btn mt-[0.75rem] w-full"
              [class.site-btn--primary]="loggedIn()"
              [class.site-btn--secondary]="!loggedIn()"
              >{{ accountLabel() }}</a
            >
          </nav>
          <!-- aria-modal: VoiceOver diyalog dışına (üstteki X) çıkamaz; kapatma diyalogun içinde de olmalı. -->
          <div class="site-container pb-6">
            <button type="button" class="site-btn site-btn--secondary w-full" (click)="closeFromMenu()" data-test="menu-close">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" class="h-5 w-5">
                <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="icons.close" />
              </svg>
              Menüyü kapat
            </button>
          </div>
        </div>
      }
    </header>
  `
})
export class SiteHeaderComponent {
  private readonly auth = inject(AuthService);
  private readonly doc = inject(DOCUMENT);

  readonly navItems = NAV_ITEMS;
  readonly icons = ICON_PATHS;
  readonly trial = TRIAL_LINK;
  readonly trialCta = TRIAL_CTA;
  readonly menuOpen = signal(false);

  /// Sunucu render'ı ve ilk istemci render'ı her zaman ziyaretçi görünümü
  /// (hydration uyuşmazlığı olmasın); giriş durumu ilk boyamadan sonra okunur.
  protected readonly loggedIn = signal(false);
  readonly accountLabel = computed(() => (this.loggedIn() ? 'Panele git' : 'Giriş yap'));
  readonly accountPath = computed(() => (this.loggedIn() ? '/dashboard' : '/auth/login'));

  @ViewChild('menuButton') private menuButton?: ElementRef<HTMLButtonElement>;

  constructor() {
    afterNextRender(() => this.loggedIn.set(this.auth.isAuthenticated()));
    inject(Router)
      .events.pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.closeMenu());
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.menuOpen()) return;
    this.closeFromMenu();
  }

  /// Panelden (Esc ya da içteki "Menüyü kapat") kapatınca odak menü düğmesine döner.
  closeFromMenu(): void {
    this.closeMenu();
    this.menuButton?.nativeElement.focus();
  }

  toggleMenu(): void {
    if (this.menuOpen()) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu(): void {
    this.menuOpen.set(true);
    this.setScrollLock(true);
  }

  closeMenu(): void {
    if (!this.menuOpen()) return;
    this.menuOpen.set(false);
    this.setScrollLock(false);
  }

  private setScrollLock(locked: boolean): void {
    this.doc.documentElement.classList.toggle('site-scroll-lock', locked);
  }
}
